import Layout from "../components/layout/Layout";
import SEO, { orgSchema, breadcrumbSchema, faqSchema, articleSchema } from "../components/layout/SEO";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Reveal } from "../components/shared/Reveal";
import CTAButton from "../components/shared/CTAButton";
import FAQAccordion from "../components/shared/FAQAccordion";
import { SummaryBox, TableOfContents, ArticleMeta, PriceTablePlaceholder, SourcesList, RelatedLinks } from "../components/shared/SeoBlocks";
import { Check, AlertTriangle } from "lucide-react";
import { SITE } from "../data/site";
import { useI18n } from "../i18n";

// Renders paragraphs, bullets and H3 subsections for a section.
function SectionBody({ section }) {
  return (
    <>
      {(section.paragraphs || []).map((p, i) => (
        <p key={`p${i}`} className="text-[17px] text-secondary-foreground leading-relaxed mb-4">{p}</p>
      ))}
      {section.bullets && section.bullets.length > 0 && (
        <ul className="my-4 space-y-2.5">
          {section.bullets.map((b, i) => (
            <li key={`b${i}`} className="flex items-start gap-3 text-[16px] text-secondary-foreground">
              <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />{b}
            </li>
          ))}
        </ul>
      )}
      {section.priceTable && <div className="my-6"><PriceTablePlaceholder rows={section.priceTable} /></div>}
      {section.callout && (
        <div className="my-6 flex items-start gap-3 bg-surface border-l-2 border-accent rounded-r-lg p-5">
          <AlertTriangle className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-[15px] text-secondary-foreground leading-relaxed">{section.callout}</p>
        </div>
      )}
      {(section.subsections || []).map((sub, i) => (
        <div key={`sub${i}`} className="mt-6">
          <h3 className="font-serif text-xl md:text-2xl text-primary mb-3">{sub.h3}</h3>
          {(sub.paragraphs || []).map((p, j) => (
            <p key={j} className="text-[16px] text-secondary-foreground leading-relaxed mb-3">{p}</p>
          ))}
          {sub.bullets && sub.bullets.length > 0 && (
            <ul className="space-y-2 mt-2">
              {sub.bullets.map((b, k) => (
                <li key={k} className="flex items-start gap-2.5 text-[15px] text-secondary-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />{b}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </>
  );
}

// Reusable template for substantial English SEO content pages.
export default function SeoContentPage({ page }) {
  const { t, lang } = useI18n();
  const noindex = lang !== "en";
  const toc = page.sections.map((s) => ({ id: s.id, h2: s.h2 }));

  return (
    <Layout>
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        noindex={noindex}
        type="article"
        schema={[
          orgSchema(),
          breadcrumbSchema([{ name: "Home", to: "/" }, ...page.breadcrumb.map((b) => ({ name: b.name, to: b.to || `/${page.slug}` }))]),
          articleSchema({ title: page.h1, description: page.metaDescription, image: `${SITE.url}/og-image.svg`, path: `/${page.slug}`, datePublished: page.published, dateModified: page.updated }),
          ...(page.faqs && page.faqs.length ? [faqSchema(page.faqs)] : []),
        ]}
      />

      <Breadcrumbs items={page.breadcrumb.map((b, i) => (i === page.breadcrumb.length - 1 ? { name: b.name } : { name: b.name, to: b.to }))} />

      <article className="container-psv py-10 md:py-14">
        <Reveal className="max-w-4xl">
          {page.eyebrow && <span className="eyebrow mb-4 block">{page.eyebrow}</span>}
          <h1 className="font-serif font-medium text-primary text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">{page.h1}</h1>
          {page.intro && <p className="mt-6 text-xl text-secondary-foreground font-light leading-relaxed">{page.intro}</p>}
          <div className="mt-6"><ArticleMeta published={page.published} updated={page.updated} reviewer={page.reviewer} /></div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Main content */}
          <div className="lg:col-span-8">
            {page.summary && <div className="mb-10"><SummaryBox>{page.summary}</SummaryBox></div>}

            {/* Mobile TOC */}
            <div className="lg:hidden mb-10"><TableOfContents sections={toc} /></div>

            {page.sections.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-28 mb-10" data-testid={`section-${section.id}`}>
                <Reveal>
                  <h2 className="font-serif text-3xl md:text-4xl text-primary mb-4 tracking-tight">{section.h2}</h2>
                  <SectionBody section={section} />
                </Reveal>
              </section>
            ))}

            {page.faqs && page.faqs.length > 0 && (
              <section id="faq" className="scroll-mt-28 mb-6">
                <Reveal>
                  <h2 className="font-serif text-3xl md:text-4xl text-primary mb-5 tracking-tight">Frequently asked questions</h2>
                  <FAQAccordion faqs={page.faqs} testIdPrefix="page-faq" />
                </Reveal>
              </section>
            )}

            <SourcesList sources={page.sources || []} />
            <RelatedLinks title={page.relatedTitle || "Related guides"} links={page.related || []} />

            <Reveal className="mt-12 bg-primary text-primary-foreground rounded-lg p-8">
              <h2 className="font-serif text-2xl md:text-3xl mb-3">Understand your options first</h2>
              <p className="text-primary-foreground/80 mb-6 max-w-xl">Request a private, no-obligation case review. We will explain the information and consultations that may be required before you make travel arrangements.</p>
              <CTAButton to="/#case-review" variant="accent" testId="content-case-review-btn">{t("cta.caseReview")}</CTAButton>
            </Reveal>

            <p className="mt-8 text-[13px] text-muted-foreground leading-relaxed">
              <strong className="text-primary">Medical disclaimer:</strong> This page provides general information only and is not medical advice, diagnosis or treatment. Suitability, risks and outcomes can only be assessed by a licensed medical professional, and final recommendations come from the treating medical professional after an in-person examination where required.
            </p>
          </div>

          {/* Sidebar: sticky TOC + CTA */}
          <aside className="hidden lg:block lg:col-span-4 lg:sticky lg:top-28 space-y-6">
            <TableOfContents sections={toc} />
            <div className="bg-surface border border-border rounded-lg p-6">
              <h2 className="font-serif text-xl text-primary mb-2">Request a case review</h2>
              <p className="text-[14px] text-muted-foreground mb-4">Private and confidential. No obligation to proceed.</p>
              <CTAButton to="/#case-review" size="sm" testId="sidebar-case-review-btn">{t("cta.caseReview")}</CTAButton>
            </div>
          </aside>
        </div>
      </article>
    </Layout>
  );
}
